import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Book, ArrowRight, Filter, LayoutGrid, Layers, Search } from 'lucide-react';
import { fetchChapters, getErrorMessage } from '../api/client';
import { chapterImageUrl } from '../utils/mediaUrls';
import { DEFAULT_LANGUAGE } from '../utils/defaultLanguage';
import ErrorMessage from './ErrorMessage';
import { useReadingProgress } from '../hooks/useReadingProgress';

function groupChaptersByYoga(chapters) {
  const groups = {};
  chapters.forEach((chapter) => {
    const yoga = chapter.yogaName || 'Other';
    if (!groups[yoga]) groups[yoga] = [];
    groups[yoga].push(chapter);
  });

  return Object.entries(groups)
    .map(([yogaName, yogaChapters]) => ({
      yogaName,
      chapters: yogaChapters.sort((a, b) => a.chapterNumber - b.chapterNumber),
      totalVerses: yogaChapters.reduce((sum, c) => sum + c.verseCount, 0)
    }))
    .sort((a, b) => a.chapters[0].chapterNumber - b.chapters[0].chapterNumber);
}

function ChapterCardSkeleton() {
  return (
    <div className="chapter-card overflow-hidden">
      <div className="skeleton h-44" style={{ borderRadius: 0 }} />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="skeleton w-12 h-12 rounded-full" />
        </div>
        <div className="skeleton h-5 w-3/4 mb-3" />
        <div className="skeleton h-3.5 w-1/2 mb-2" />
        <div className="skeleton h-3.5 w-2/3 mb-4" />
        <div className="skeleton h-12 w-full mb-4" />
        <div className="skeleton h-9 w-28 rounded-xl" />
      </div>
    </div>
  );
}

function ChapterCard({ chapter, readVerses }) {
  const progress = useMemo(() => {
    if (!chapter.verseCount || !readVerses) return 0;
    let count = 0;
    for (let i = 1; i <= chapter.verseCount; i++) {
      if (readVerses.has(`${chapter.chapterNumber}:${i}`)) count++;
    }
    return count / chapter.verseCount;
  }, [chapter, readVerses]);

  const circumference = 2 * Math.PI * 22;
  const readCount = Math.round(progress * chapter.verseCount);

  return (
    <div className="chapter-card">
      <img
        src={chapterImageUrl(chapter.chapterNumber)}
        alt={`Chapter ${chapter.chapterNumber}: ${chapter.chapterName}`}
        className="chapter-image"
        loading="lazy"
      />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="relative w-12 h-12 shrink-0">
            <div className="w-12 h-12 bg-gita-saffron rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">{chapter.chapterNumber}</span>
            </div>
            {progress > 0 && (
              <svg className="absolute inset-0 pointer-events-none" viewBox="0 0 48 48" aria-hidden="true">
                <circle cx="24" cy="24" r="22" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
                <circle
                  cx="24" cy="24" r="22"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * (1 - progress)}
                  transform="rotate(-90 24 24)"
                />
              </svg>
            )}
          </div>
          <span className="text-sm text-gray-500">
            {progress > 0 ? `${readCount} / ${chapter.verseCount}` : `${chapter.verseCount} verses`}
          </span>
        </div>

        <h3 className="chapter-title mb-2">{chapter.chapterName}</h3>

        <div className="space-y-2 mb-4">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Yoga:</span> {chapter.yogaName}
          </p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">Meaning:</span> {chapter.meaning}
          </p>
        </div>

        <p className="text-gray-700 text-sm mb-4 line-clamp-3">{chapter.summary}</p>

        <Link to={`/chapter/${chapter.chapterNumber}`} className="btn-primary text-sm flex items-center">
          View Details
          <ArrowRight className="ml-1 w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

const Chapters = () => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_LANGUAGE);
  const [viewMode, setViewMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { readVerses } = useReadingProgress();

  const loadChapters = useCallback(async (language) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChapters(language);
      setChapters(data.sort((a, b) => a.chapterNumber - b.chapterNumber));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChapters(selectedLanguage);
  }, [selectedLanguage, loadChapters]);

  const filteredChapters = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return chapters;
    return chapters.filter(
      (c) =>
        String(c.chapterNumber).includes(q) ||
        c.chapterName?.toLowerCase().includes(q) ||
        c.yogaName?.toLowerCase().includes(q) ||
        c.meaning?.toLowerCase().includes(q)
    );
  }, [chapters, searchQuery]);

  const yogaGroups = useMemo(() => groupChaptersByYoga(filteredChapters), [filteredChapters]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {Array.from({ length: 6 }).map((_, i) => (
          <ChapterCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={() => loadChapters(selectedLanguage)} />;
  }

  return (
    <div className="space-y-8">
      <div className="page-header">
        <p className="page-header-om" aria-hidden="true">ॐ</p>
        <h1 className="page-header-title">Chapters</h1>
        <p className="page-header-desc">
          Explore the 18 chapters of the Bhagavad Gita — browse all chapters or group
          them by yoga path (Karma, Bhakti, Jnana).
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
        <div className="flex items-center gap-2 bg-white/90 rounded-xl shadow-gita border border-gita-gold/20 px-3 py-2 w-full sm:w-64 min-h-[44px]">
          <Search className="w-4 h-4 text-gita-muted shrink-0" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chapters…"
            className="flex-1 border-0 focus:ring-0 focus:outline-none bg-transparent text-gita-ink text-sm"
          />
        </div>
        <div className="flex items-center space-x-2 bg-white/90 rounded-xl shadow-gita border border-gita-gold/20 p-2 w-full sm:w-auto">
          <Filter className="w-4 h-4 text-gita-muted shrink-0" />
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="px-3 py-2 border-0 focus:ring-0 focus:outline-none bg-transparent text-gita-ink w-full min-h-[44px]"
          >
            <option value="te">Telugu</option>
            <option value="hi">Hindi</option>
            <option value="en">English</option>
          </select>
        </div>

        <div className="bg-white/90 rounded-xl shadow-gita border border-gita-gold/20 p-1 flex flex-col sm:flex-row w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setViewMode('all')}
            className={`px-3 sm:px-4 py-2.5 rounded-md font-medium transition-colors min-h-[44px] ${
              viewMode === 'all'
                ? 'bg-gita-saffron text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <LayoutGrid className="w-4 h-4 inline mr-2" />
            All Chapters
          </button>
          <button
            type="button"
            onClick={() => setViewMode('yoga')}
            className={`px-3 sm:px-4 py-2.5 rounded-md font-medium transition-colors min-h-[44px] ${
              viewMode === 'yoga'
                ? 'bg-gita-saffron text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Layers className="w-4 h-4 inline mr-2" />
            By Yoga
          </button>
        </div>
      </div>

      {viewMode === 'all' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredChapters.map((chapter) => (
            <ChapterCard key={chapter._id} chapter={chapter} readVerses={readVerses} />
          ))}
        </div>
      )}

      {viewMode === 'yoga' && (
        <div className="space-y-10">
          {yogaGroups.map((group) => (
            <section key={group.yogaName}>
              <div className="card mb-6 bg-gradient-to-r from-gita-cream to-gita-parchment border-gita-gold/25">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h2 className="section-heading">{group.yogaName}</h2>
                    <p className="text-gray-600 text-sm mt-1">
                      Chapters {group.chapters[0].chapterNumber}–
                      {group.chapters[group.chapters.length - 1].chapterNumber}
                    </p>
                  </div>
                  <div className="text-sm text-gray-600">
                    {group.chapters.length} chapters · {group.totalVerses} verses
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {group.chapters.map((chapter) => (
                  <ChapterCard key={chapter._id} chapter={chapter} readVerses={readVerses} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {filteredChapters.length === 0 && (
        <div className="text-center py-12">
          <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">
            {searchQuery ? `No chapters match "${searchQuery}".` : 'No chapters found for the selected language.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Chapters;
